import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import { PRIVACY_POLICY_URL } from '../constants/constants';
import { appTheme } from '../talkTreeShared/Tree/constants/theme';

const sections = [
  {
    title: '1. Who We Are',
    body: [
      'Talk Trees provides tools and services intended to support educational workflows and related communication, recording, and content management activities.',
    ],
  },
  {
    title: '2. Eligibility',
    body: [
      'You may use the Services only if you are able to form a binding agreement under applicable law and are authorized to use the Services on behalf of yourself or your organization, if applicable.',
      'If you are using the Services for an organization, you represent that you have authority to bind that organization to these Terms.',
    ],
  },
  {
    title: '3. Educational Institutional Use',
    body: [
      'Talk Trees\' data practices are designed to support compliance with FERPA and COPPA; however, responsibility for obtaining required institutional and parental consents rests with the teacher or institution.',
    ],
  },
  {
    title: '4. Your Account',
    body: [
      'You are responsible for maintaining the confidentiality of your login credentials, all activity that occurs under your account, and providing accurate information when creating or using an account.',
      'You must notify Talk Trees promptly if you believe your account has been accessed without authorization.',
    ],
  },
  {
    title: '5. Acceptable Use',
    body: [
      'You agree not to use the Services for any unlawful, harmful, fraudulent, or deceptive purpose, interfere with the operation or security of the Services, attempt to gain unauthorized access to accounts or systems, or upload content that infringes another party\'s rights.',
    ],
  },
  {
    title: '6. Recording Consent',
    body: [
      'You are solely responsible for ensuring that any recording you make using the Services complies with all applicable laws, including federal and state laws governing the recording of conversations.',
      'Laws governing recording consent vary by jurisdiction. By recording through the Services, you represent that you have obtained all legally required consents from participants or that applicable law otherwise permits the recording.',
    ],
  },
  {
    title: '7. User Content',
    body: [
      'You retain ownership of content you provide through the Services, including account information, recordings, text, and other materials.',
      'By submitting content to the Services, you grant Talk Trees a limited license to host, store, process, transmit, and display that content solely as needed to operate and provide the Services. Talk Trees does not use recordings or transcripts to train machine learning models and does not retain audio files beyond what is necessary to generate transcriptions.',
      'You are responsible for ensuring that you have all necessary rights and permissions for any content you submit.',
    ],
  },
  {
    title: '8. Privacy',
    body: [
      'Your use of the Services is also subject to the Talk Trees Privacy Policy.',
    ],
  },
  {
    title: '9. Third-Party Services',
    body: [
      'The Services may integrate with third-party services, including authentication providers such as Google and infrastructure providers such as Amazon Web Services. Your use of those services may also be governed by their own terms and policies.',
    ],
  },
  {
    title: '10. Service Availability',
    body: [
      'Talk Trees may update, modify, suspend, or discontinue all or part of the Services at any time. The Services are provided on an "as is" and "as available" basis without a guarantee of uninterrupted or error-free operation.',
    ],
  },
  {
    title: '11. Intellectual Property',
    body: [
      'The Services, including software, design, branding, and related materials, are owned by Talk Trees or its licensors and are protected by applicable intellectual property laws.',
      'Except as expressly allowed by these Terms, you may not copy, modify, distribute, sell, lease, reverse engineer, or create derivative works from the Services.',
    ],
  },
  {
    title: '12. Limitation of Liability',
    body: [
      'To the maximum extent permitted by law, Talk Trees and its affiliates, officers, employees, and service providers will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, revenue, profits, or goodwill, arising out of or related to your use of the Services.',
    ],
  },
  {
    title: '13. Termination',
    body: [
      'Talk Trees may suspend or terminate your access to the Services if it believes you have violated these Terms, created risk for Talk Trees or others, or if required by law.',
      'You may stop using the Services at any time.',
    ],
  },
  {
    title: '14. Changes to These Terms',
    body: [
      'Talk Trees may update these Terms from time to time. Continued use of the Services after updated Terms become effective means you accept the revised Terms.',
    ],
  },
  {
    title: '15. Governing Law',
    body: [
      'These Terms are governed by the laws of the State of Colorado, without regard to conflict of laws principles.',
    ],
  },
  {
    title: '16. Contact',
    body: [
      'If you have questions about these Terms, contact Talk Trees at support@talktrees.ai.',
    ],
  },
];

export default function TermsOfService() {
  return (
    <ThemeProvider theme={appTheme}>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#F6F8FB', py: { xs: 3, md: 6 } }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 5 },
              borderRadius: 4,
              border: '1px solid #E5E7EB',
              backgroundColor: '#ffffff',
              boxShadow: '0 12px 40px rgba(10, 38, 71, 0.08)',
            }}
          >
            <Stack spacing={3}>
              <Box>
                {/* <Typography variant="overline" sx={{ color: '#1F72E7', fontWeight: 700, letterSpacing: 1.2 }}>
                  Talk Trees
                </Typography> */}

                <Box
                  component="img"
                  src="/TTLogo.svg"
                  alt="Talk Trees"
                  sx={{ height: { xs: 30, sm: 38 }, width: 'auto', display: 'block' }}
                />
                
                <Typography variant="h4" sx={{ mt: 1, fontWeight: 700, color: '#111827', textAlign: 'left' }}>
                  Terms of Service
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: '#6B7280', textAlign: 'left' }}>
                  Effective date: June 24, 2026
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.75, textAlign: 'left' }}>
                These Terms of Service govern your access to and use of the Talk Trees website,
                applications, and related services. By accessing or using the Services, you agree
                to these Terms. If you do not agree to these Terms, do not use the Services.
              </Typography>

              {sections.map((section) => (
                <Box key={section.title}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, color: '#111827', textAlign: 'left' }}>
                    {section.title}
                  </Typography>
                  <Stack spacing={1.5}>
                    {section.body.map((paragraph) => (
                      <Typography key={paragraph} variant="body1" sx={{ color: '#374151', lineHeight: 1.75, textAlign: 'left' }}>
                        {paragraph}
                      </Typography>
                    ))}
                    {section.title === '6. Privacy' && (
                      <Link
                        href={PRIVACY_POLICY_URL}
                        target="talk-trees-privacy"
                        rel="noreferrer"
                        underline="always"
                        sx={{ color: '#1F72E7', fontWeight: 600, width: 'fit-content' }}
                      >
                        View Privacy Policy
                      </Link>
                    )}
                  </Stack>
                </Box>
              ))}

            </Stack>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
